import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ADMIN_DEFAULT_LANGUAGE,
  ADMIN_LANGUAGES,
  ADMIN_TRANSLATIONS,
  type AdminLanguageCode,
  type AdminTranslationKey,
} from '@/i18n/adminCatalog';

type AdminTranslationParams = Record<string, string | number>;

interface AdminI18nContextValue {
  language: AdminLanguageCode;
  languages: typeof ADMIN_LANGUAGES;
  locale: string;
  setLanguage: (language: AdminLanguageCode) => void;
  t: (key: AdminTranslationKey, params?: AdminTranslationParams) => string;
  formatNumber: (value: number | null | undefined) => string;
}

const STORAGE_KEY = 'tg-crypto-admin-language';
const AdminI18nContext = createContext<AdminI18nContextValue | null>(null);

function isAdminLanguageCode(value: string | null): value is AdminLanguageCode {
  return ADMIN_LANGUAGES.some((language) => language.code === value);
}

function getInitialLanguage(): AdminLanguageCode {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isAdminLanguageCode(stored)) return stored;

  const browserLanguage = window.navigator.language.split('-')[0];
  if (isAdminLanguageCode(browserLanguage)) return browserLanguage;

  return ADMIN_DEFAULT_LANGUAGE;
}

function interpolate(template: string, params?: AdminTranslationParams) {
  if (!params) return template;

  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params[key] === undefined ? `{{${key}}}` : String(params[key]),
  );
}

export function AdminI18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AdminLanguageCode>(getInitialLanguage);

  const metadata = ADMIN_LANGUAGES.find((item) => item.code === language) ?? ADMIN_LANGUAGES[0];
  const locale = metadata.locale;

  const setLanguage = useCallback((nextLanguage: AdminLanguageCode) => {
    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (key: AdminTranslationKey, params?: AdminTranslationParams) => {
      const dictionary = ADMIN_TRANSLATIONS[language] ?? ADMIN_TRANSLATIONS[ADMIN_DEFAULT_LANGUAGE];
      const fallback = ADMIN_TRANSLATIONS[ADMIN_DEFAULT_LANGUAGE][key] ?? key;
      return interpolate(dictionary[key] ?? fallback, params);
    },
    [language],
  );

  const formatNumber = useCallback(
    (value: number | null | undefined) => new Intl.NumberFormat(locale).format(Number(value || 0)),
    [locale],
  );

  const value = useMemo<AdminI18nContextValue>(
    () => ({
      language,
      languages: ADMIN_LANGUAGES,
      locale,
      setLanguage,
      t,
      formatNumber,
    }),
    [formatNumber, language, locale, setLanguage, t],
  );

  return <AdminI18nContext.Provider value={value}>{children}</AdminI18nContext.Provider>;
}

export function useAdminI18n() {
  const context = useContext(AdminI18nContext);
  if (!context) {
    throw new Error('useAdminI18n must be used within an AdminI18nProvider');
  }
  return context;
}
